import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type {
    ChartAccount,
    LocaleCode,
    OperationHistoryVersion,
    OperationTemplateHistory,
} from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { ChangeLog, VersionTimeline } from './components';
import type { TimelineEntry } from './historyMeta';

type HistoryDrawerViewProps = {
    /** The template on screen; `null` keeps the drawer closed. */
    operationKey: string | null;
    onClose: () => void;
    locale: LocaleCode;
    isLoading: boolean;
    hasError: boolean;
    history: OperationTemplateHistory | null;
    timeline: TimelineEntry[];
    accounts: ChartAccount[];
    expanded: number[];
    onToggle: (version: number) => void;
    onOpenActivity: () => void;
    draftVersion: OperationHistoryVersion | null;
    canEdit: boolean;
    /** `null` from a host that is already the editor — see the note below. */
    onOpenEditor: ((key: string) => void) | null;
};

/**
 * The version history — render only.
 *
 * A right-hand `Sheet` over whichever screen asked for it, rather than the
 * sub-page this used to be. The history answers a question about the screen a
 * reader is already on — *why does this operation post the way it does?* — and
 * a full navigation made them leave the catalog to ask it and come back to find
 * their search and filter reset.
 *
 * A single column: the timeline of versions with their diffs, then the change
 * log underneath. One column rather than two because the halves answer the same
 * question in sequence — what the rules were, and who decided them — and reading
 * them side by side would mean scanning across for a date already on the card.
 *
 * **The footer's "open the draft" is offered by the host, not assumed.** Opened
 * from the catalog it is the way through to the editor; opened *inside* the
 * editor there is nothing to navigate to, so the host passes `null` and the
 * button is absent rather than present and inert.
 *
 * The heading and the footer note keep their real text through the load, and
 * only the cards skeleton — the loading-state standard.
 */
export const HistoryDrawerView: React.FC<HistoryDrawerViewProps> = ({
    operationKey,
    onClose,
    locale,
    isLoading,
    hasError,
    history,
    timeline,
    accounts,
    expanded,
    onToggle,
    onOpenActivity,
    draftVersion,
    canEdit,
    onOpenEditor,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Sheet
            isOpen={operationKey !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[720px]">
                    <Sheet.Dialog
                        aria-label={t('operations.history.title')}
                        {...getNodeTestIdProp('operations-history-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span className="text-lg font-semibold">
                                        {t('operations.history.title')}
                                    </span>
                                    <span
                                        className="text-sm font-normal text-muted"
                                        {...getNodeTestIdProp(
                                            'operations-history-summary',
                                        )}
                                    >
                                        {history
                                            ? t('operations.history.summary', {
                                                  name:
                                                      locale === 'ka'
                                                          ? history.nameKa
                                                          : history.nameEn,
                                                  count: history.versions
                                                      .length,
                                              })
                                            : t('operations.history.subtitle')}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-6">
                            {hasError ? (
                                <p
                                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        'operations-history-error',
                                    )}
                                >
                                    {t('operations.history.error.message')}
                                </p>
                            ) : (
                                <>
                                    <VersionTimeline
                                        accounts={accounts}
                                        entries={timeline}
                                        expanded={expanded}
                                        isLoading={isLoading}
                                        locale={locale}
                                        onOpenActivity={onOpenActivity}
                                        onToggle={onToggle}
                                    />

                                    <ChangeLog
                                        events={history?.events ?? []}
                                        isLoading={isLoading}
                                    />
                                </>
                            )}
                        </Sheet.Body>

                        <Sheet.Footer className="flex flex-wrap items-center justify-between gap-3">
                            <p
                                className="max-w-md text-xs text-muted"
                                {...getNodeTestIdProp(
                                    'operations-history-footer',
                                )}
                            >
                                {t('operations.history.footer')}
                            </p>
                            {onOpenEditor &&
                            operationKey &&
                            draftVersion &&
                            canEdit ? (
                                <Button
                                    onPress={() => onOpenEditor(operationKey)}
                                    variant="primary"
                                    {...getNodeTestIdProp(
                                        'operations-history-open-draft',
                                    )}
                                >
                                    {t('operations.history.openDraft', {
                                        number: draftVersion.version,
                                    })}
                                </Button>
                            ) : null}
                        </Sheet.Footer>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
